import clsx from 'clsx'
import NextImage from 'next/legacy/image'

import { useMemo, type ChangeEvent, type FunctionComponent } from 'react'
import { useTranslate } from '../../contexts/TranslationContext'
import SvgCross from '../../svg/icons/Cross'
import { Hidden } from '../shared/Hidden'
import style from './Form.module.sass'
import { RadioInput } from './RadioInput'

type RadioGroupProps = {
	radioOptions:
		| string[]
		| {
				id: string
				label: string
				description?: string
				value?: number
				imageFrom?: string
				imageTo?: string
				imageMain?: string
		  }[]
		| { id: string; amount?: number; unit?: string }[]
		| { id: string; week?: number }[]
	inRow?: boolean
	inGrid?: boolean
	inBlock?: boolean
	inGridSmall?: boolean
	title: React.ReactNode
	name?: string
	value?: string
	mobileTooltip?: JSX.Element
	tooltipText?: JSX.Element
	isCentered?: boolean
	isNullAllowed?: boolean
	nullClick?: () => void
	extraContent?: JSX.Element
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

export const RadioGroup: FunctionComponent<RadioGroupProps> = ({
	radioOptions,
	inRow = false,
	inGrid = false,
	inBlock = false,
	inGridSmall = false,
	title,
	name,
	tooltipText,
	mobileTooltip,
	isCentered,
	onChange,
	nullClick,
	value,
	extraContent,
	isNullAllowed = false,
}) => {
	const options = useMemo(() => {
		return radioOptions.map((o) => {
			if (typeof o === 'string') {
				return {
					id: o,
					label: o,
				}
			}
			const label = 'label' in o ? o.label : 'week' in o ? o.week : 'unit' in o ? `${o.amount} ${o.unit}` : o.id
			const description = 'description' in o ? o.description : ''
			const value = 'value' in o ? o.value : ''
			const imageTo = 'imageTo' in o ? o.imageTo : ''
			const imageMain = 'imageMain' in o ? o.imageMain : ''
			const imageFrom = 'imageFrom' in o ? o.imageFrom : ''

			return {
				id: o.id,
				label: String(label ?? o.id),
				description,
				value,
				imageTo,
				imageFrom,
				imageMain,
			}
		})
	}, [radioOptions])

	const optionsContainImage = options.filter((o) => o.imageFrom || o.imageFrom || o.imageTo).length > 0

	const translate = useTranslate()
	return (
		<div className={style.inputWrapper}>
			<div className={clsx(style.titleNullable, isCentered && style.titleCentered)} data-tip>
				{isCentered ? (
					<span className={style.titleComplet}>
						<h3 className={style.titleRegular}>{title}</h3>{' '}
						<span className={style.titleCompletExtra}>{extraContent}</span>
					</span>
				) : (
					<span className={style.titleComplet}>
						<h2 className={style.titleRegular}>{title}</h2>{' '}
						<span className={style.titleCompletExtra}>{extraContent}</span>
					</span>
				)}
				<Hidden breakpoint="mobileUp">
					{isNullAllowed && (
						<button onClick={nullClick} className={style.nullButton}>
							<SvgCross />
						</button>
					)}
				</Hidden>
			</div>
			<fieldset
				className={clsx(
					style.fieldset,
					inGrid && style.inGrid,
					inGridSmall && style.inGridSmall,
					inRow && style.inRow,
					isCentered && style.fullWidth,
					inGridSmall && inRow && style.inRowMobile,
					inBlock && style.inBlock,
					isNullAllowed && style.isNullAllowed,
					optionsContainImage && style.fieldsetBodyType,
				)}
			>
				{options.map((option) =>
					option.description ? (
						<RadioInput
							inGrid={inGrid}
							key={option.id}
							inGridSmall={inGridSmall}
							name={name}
							onChange={onChange}
							label={option.value ? `${option.value}. ${option.label}` : option.label}
							value={option.id}
							checked={value === option.id}
							mobileTooltip={
								<div className={clsx(style.tooltipContentBodyTypeMobile)}>
									{option.imageMain ? (
										<div className={style.tooltipImage}>
											<NextImage
												src={option.imageMain}
												alt={translate('alternativeText.dogSilouette')}
												title={option.label}
												layout="fill"
												objectFit="cover"
											/>
										</div>
									) : (
										<>
											{option.imageFrom && (
												<div className={style.tooltipImage}>
													<NextImage
														src={option.imageFrom}
														alt={translate('alternativeText.dogSilouette')}
														title={option.label}
														layout="fill"
														objectFit="cover"
													/>
												</div>
											)}
											<div className={style.betweenText}>{translate('radioBodyTypeGroup.tooltip.betweenText')}</div>
											{option.imageTo && (
												<div className={style.tooltipImage}>
													<NextImage
														src={option.imageTo}
														title={option.label}
														alt={translate('alternativeText.dogSilouette')}
														layout="fill"
														objectFit="contain"
													/>
												</div>
											)}
										</>
									)}
									<div className={style.tooltipTextBodyType}>
										{option.description}
										<div className={style.tooltipTextNote}>{translate('radioBodyTypeGroup.tooltip.note')}</div>
									</div>
								</div>
							}
							tooltipHovered={<span className={style.tooltipIcon}>?</span>}
							tooltipText={
								<div
									className={clsx(
										style.tooltipContentBodyType,
										option.imageFrom && option.imageTo && style.tooltipContentBodyTypeMultiple,
									)}
								>
									<div className={style.tooltipContentTitle}>
										{option.value}. {option.label}{' '}
									</div>
									<div
										className={clsx(
											style.contentGroup,
											option.imageFrom && option.imageTo && style.contentGroupMultiple,
										)}
									>
										<div className={style.tooltipImageGroup}>
											{option.imageMain ? (
												<div className={style.tooltipImage}>
													<NextImage
														src={option.imageMain}
														alt={translate('alternativeText.dogSilouette')}
														title={option.label}
														layout="fill"
														objectFit="cover"
													/>
												</div>
											) : (
												<>
													{option.imageFrom && (
														<div className={style.tooltipImage}>
															<NextImage
																src={option.imageFrom}
																alt={translate('alternativeText.dogSilouette')}
																title={option.label}
																layout="fill"
																objectFit="cover"
															/>
														</div>
													)}
													<div className={style.betweenText}>{translate('radioBodyTypeGroup.tooltip.betweenText')}</div>
													{option.imageTo && (
														<div className={style.tooltipImage}>
															<NextImage
																src={option.imageTo}
																title={option.label}
																alt={translate('alternativeText.dogSilouette')}
																layout="fill"
																objectFit="contain"
															/>
														</div>
													)}
												</>
											)}
										</div>
										<div className={style.tooltipTextBodyType}>
											{option.description}
											<div className={style.tooltipTextNote}>{translate('radioBodyTypeGroup.tooltip.note')}</div>
										</div>
									</div>
								</div>
							}
						/>
					) : (
						<RadioInput
							inGrid={inGrid}
							key={option.id}
							inGridSmall={inGridSmall}
							name={name}
							onChange={onChange}
							label={option.label}
							value={option.id}
							checked={value === option.id}
							mobileTooltip={mobileTooltip}
							tooltipText={tooltipText}
						/>
					),
				)}
				<Hidden breakpoint="mobileDown">
					{isNullAllowed && (
						<button onClick={nullClick} className={style.nullButton}>
							<SvgCross />
						</button>
					)}
				</Hidden>
			</fieldset>
		</div>
	)
}
